import { useState } from 'react';
import { api, describeApiError } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { COMPLIANCE_FIELD_LABEL, type NoteProposal } from '../../../shared/noteActions';

type TaskDraft = { on: boolean; text: string; owner: string; due: string; source: string };

/**
 * Proposals read from a meeting note (AI or rules, labelled). Nothing is applied until ticked; "File note and
 * apply" files the note with the ticked items (compliance dates, next review, RoA scope)
 * and sends the ticked tasks to Next-Task.
 */
export function NoteActionsReview({ clientId, note, noteType, proposal, reader = 'rules', fallbackReason = null, onDone, onCancel }: {
  clientId: string; note: string; noteType: string; proposal: NoteProposal; reader?: 'ai' | 'rules'; fallbackReason?: string | null;
  onDone: (msg: string) => void; onCancel: () => void;
}) {
  const [tasks, setTasks] = useState<TaskDraft[]>(proposal.tasks.map((t) => ({ on: true, text: t.text, owner: t.owner ?? '', due: t.due ?? '', source: t.source })));
  const [comp, setComp] = useState(proposal.compliance.map((c) => ({ ...c, on: true })));
  const [review, setReview] = useState(proposal.reviewDue ? { on: true, date: proposal.reviewDue.date, source: proposal.reviewDue.source } : null);
  const [scope, setScope] = useState(proposal.roaScope.map((s) => ({ on: true, text: s })));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const nothing = !tasks.length && !comp.length && !review && !scope.length;

  async function apply() {
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ data: { tasks: Array<{ sent: boolean }> } }>(`/api/clients/${clientId}/note-actions/apply`, {
        method: 'POST',
        body: JSON.stringify({
          note, noteType: ['meeting', 'call', 'other'].includes(noteType) ? noteType : 'other', reader,
          compliance: comp.filter((c) => c.on).map((c) => ({ field: c.field, date: c.date })),
          reviewDue: review?.on ? review.date : null,
          roaScope: scope.filter((s) => s.on && s.text.trim()).map((s) => s.text.trim()),
          tasks: tasks.filter((t) => t.on && t.text.trim()).map((t) => ({ text: t.text.trim(), owner: t.owner.trim() || null, due: t.due || null })),
        }),
      });
      const sent = r.data.tasks.filter((t) => t.sent).length;
      const total = r.data.tasks.length;
      onDone(`Note filed.${total ? ` ${sent} of ${total} task${total === 1 ? '' : 's'} sent to Next-Task.` : ''}`);
    } catch (e) { setMsg(describeApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div className="mt-4 border-t border-hairline-paper pt-4" data-testid="note-actions">
      <p className="qa-eyebrow mb-3">Found in the note</p>
      {nothing && <p className="text-sm text-ink-mute">No actions, dates or advice found. You can still file the note as it is.</p>}

      {tasks.length > 0 && (
        <fieldset className="mb-4">
          <legend className="text-xs font-semibold text-ink-soft mb-2">Tasks (to Next-Task)</legend>
          <ul className="space-y-2">
            {tasks.map((t, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                <input type="checkbox" checked={t.on} aria-label={`Include task ${t.text}`} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} />
                <input className="qa-input text-sm flex-1 min-w-[12rem]" aria-label={`Task ${i + 1}`} value={t.text} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                <input className="qa-input text-sm w-28" aria-label={`Owner for task ${i + 1}`} placeholder="Owner" value={t.owner} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, owner: e.target.value } : x)))} />
                <input type="date" className="qa-input text-sm" aria-label={`Due date for task ${i + 1}`} value={t.due} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? { ...x, due: e.target.value } : x)))} />
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {comp.length > 0 && (
        <fieldset className="mb-4">
          <legend className="text-xs font-semibold text-ink-soft mb-2">Compliance dates</legend>
          {comp.map((c, i) => (
            <label key={c.field} className="flex items-start gap-2 text-sm py-1">
              <input type="checkbox" className="mt-1" checked={c.on} onChange={(e) => setComp(comp.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} />
              <span><strong>{COMPLIANCE_FIELD_LABEL[c.field]}</strong> done {formatDateAU(c.date)} <span className="text-ink-mute">from "{c.source}"</span></span>
            </label>
          ))}
        </fieldset>
      )}

      {review && (
        <fieldset className="mb-4">
          <legend className="text-xs font-semibold text-ink-soft mb-2">Next review</legend>
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <input type="checkbox" checked={review.on} aria-label="Set the next review date" onChange={(e) => setReview({ ...review, on: e.target.checked })} />
            <input type="date" className="qa-input text-sm" aria-label="Next review date" value={review.date} onChange={(e) => setReview({ ...review, date: e.target.value })} />
            <span className="text-ink-mute text-xs">from "{review.source}"</span>
          </label>
        </fieldset>
      )}

      {scope.length > 0 && (
        <fieldset className="mb-4">
          <legend className="text-xs font-semibold text-ink-soft mb-2">RoA scope (flags the client RoA needed)</legend>
          {scope.map((s, i) => (
            <label key={i} className="flex items-start gap-2 text-sm py-1">
              <input type="checkbox" className="mt-1" checked={s.on} onChange={(e) => setScope(scope.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} />
              <span>{s.text}</span>
            </label>
          ))}
        </fieldset>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary text-sm" disabled={busy} onClick={apply}>{busy ? 'Filing...' : 'File note and apply ticked'}</button>
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>Back to the note</button>
      </div>
      {msg && <p role="alert" className="text-xs text-rust mt-2">{msg}</p>}
      <p className="text-[11px] text-ink-mute mt-2" data-testid="note-reader">
        {reader === 'ai'
          ? 'Read by AI. Names, emails, phone and account numbers were removed before the note was sent; check each item before applying.'
          : fallbackReason
            ? `Read by rules in Client-Base (the AI reader was unavailable: ${fallbackReason}).`
            : 'Read by rules in Client-Base; the note did not leave the app.'}
      </p>
    </div>
  );
}
